<script lang="ts" setup>
import CurrentLanguage from '@/components/CurrentLanguage.vue';
import { useEvents, Event } from '@/services/events.composable';
import { Actions, Mutations, ShiftCMSState } from '@/store';
import { LocationsActions, LocationsState } from '@/stores/locations.store';
import { NEW_ID } from '@/util/constants';
import { computed, onMounted, PropType, ref, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import {
  Location as LocationModel,
  LocationType,
  nameRules,
  locationTypes,
} from '@/models/location.model';
import locationsService from '@/services/locations.service';

const store = useStore<ShiftCMSState & { los: LocationsState }>();

const events = useEvents();

const props = defineProps({
  data: {
    type: Object as PropType<LocationModel>,
  },
});

const valid = ref<boolean>(false);

const location = ref<LocationModel>({
  id: NEW_ID,
  name: '',
  type: LocationType.City,
  lat: 0,
  lng: 0,
  radius: 0,
  parentLocation: undefined,
});

const action = computed<string>(() =>
  location.value.id === NEW_ID ? 'Add' : 'Save',
);

const saveLocation = async () => {
  let success: boolean = false;
  if (valid.value) {
    if (location.value.id === NEW_ID) {
      success = await store.dispatch(
        LocationsActions.AddLocation,
        location.value,
      );
    } else {
      success = await store.dispatch(
        LocationsActions.UpdateLocation,
        location.value,
      );
    }
  }
  if (success) {
    events.emit(Event.Saved, true);
    store.dispatch(Actions.ShowNotification, {
      message: 'Saved Location',
      type: 'success',
    });
    store.commit(Mutations.HideDrawer);
    store.dispatch(LocationsActions.GetLocations);
  } else {
    store.dispatch(Actions.ShowNotification, {
      message: 'Failed to save Location',
      type: 'error',
    });
  }
};

const updateLanguage = async () => {
  if (location.value.id === NEW_ID) {
    Object.assign(location, { name: '' });
  } else {
    const item = await store.dispatch(
      LocationsActions.GetLocation,
      location.value.id,
    );

    if (item) {
      Object.assign(location.value, {
        ...item,
        startDate: new Date(item.startDate || new Date().toISOString()),
        endDate: new Date(item.endDate || new Date().toISOString()),
      });
    }
  }
};

onBeforeUnmount(() => {
  events.off(Event.LanguageChanged, updateLanguage);
});

onMounted(() => {
  events.on(Event.LanguageChanged, updateLanguage);

  if (props.data) {
    Object.assign(location.value, {
      ...props.data,
    });
  }
});

const removeLocation = async () => {
  if (
    location.value &&
    location.value.id !== NEW_ID &&
    confirm('Are you absolutely sure you want to delete this "Location"?')
  ) {
    store.dispatch(LocationsActions.DeleteLocation, location.value.id);

    store.commit(Mutations.HideDrawer);
  }
};

const search = ref<string>('');

const findCoordinates = async () => {
  const result = await locationsService.findCoordinates(search.value);

  if (result.status && result.data.lat !== 0) {
    location.value.lat = result.data.lat;
    location.value.lng = result.data.lng;
  } else {
    store.dispatch(Actions.ShowNotification, {
      message: `No coordinates found for ${search.value}`,
      type: 'warning',
    });
  }
};

const checkCoordinates = () => {
  window.open(
    `http://www.google.com/maps/place/${location.value.lat},${location.value.lng}`,
    '_new',
  );
};
</script>
<template>
  <v-form v-model="valid" @submit.prevent="saveLocation">
    <v-container>
      <h2>{{ $t(action) }} a event item <CurrentLanguage /></h2>
      <v-row>
        <v-col>
          <v-text-field
            v-model="location.name"
            :rules="nameRules"
            label="Name"
            required
          ></v-text-field>
        </v-col>
        <v-col>
          <v-select
            label="Type"
            v-model="location.type"
            :items="locationTypes"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="5"
          ><v-text-field v-model.number="location.lat" label="Latitude" />
        </v-col>
        <v-col cols="5"
          ><v-text-field v-model.number="location.lng" label="Longitude" />
        </v-col>
        <v-col cols="2">
          <v-btn @click="checkCoordinates" color="info">Check</v-btn>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="10">
          <v-text-field v-model="search" label="Search for coordinates" />
        </v-col>
        <v-col class="pt-5">
          <v-btn
            block
            @click="findCoordinates"
            color="info"
            :disabled="!search.length"
            >Search</v-btn
          >
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-text-field v-model.number="location.radius" label="Radius" />
        </v-col>
      </v-row>

      <v-row>
        <v-col col="2"></v-col>
        <v-col>
          <v-btn
            class="mt-2"
            type="button"
            color="warning"
            @click="removeLocation"
            block
            >Delete</v-btn
          >
        </v-col>
        <v-col>
          <v-btn class="mt-2" type="reset" block>Reset</v-btn>
        </v-col>
        <v-col>
          <v-btn class="mt-2" type="submit" color="success" block>{{
            action
          }}</v-btn>
        </v-col>
        <v-col col="2"></v-col>
      </v-row>
    </v-container>
  </v-form>
</template>
<style scoped>
pre {
  font-family: sans-serif;
  font-size: 12px;
  background-color: rgb(255, 230, 171);
  display: block;
  height: 100%;
  padding: 8px;
}
</style>
